/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getPageZoomLevel } from "selectors/zoom";
import {
  getPageHeight,
  getPageWidth,
  isLeftPageIndex,
  getRowIndex,
  getRowHeight,
  getPagesInRow
} from "selectors/pageSelectors";
import { PAGE_BOTTOM_MARGIN } from "constants/constants";
import * as constants from "constants/constants";
import { toJS } from "mobx";
import { isFirstDCAPIRenditionLoaded } from "selectors/dcapiSelectors";
import { getScrollbarSize } from "selectors/appUiStateSelectors";
import {
  getViewPortHeightWithoutPagesViewScrollbar,
  getLastPageExtraBottomSpace,
  getViewPortWidth,
  getViewPortHeight
} from "selectors/documentViewSelectors";
import {
  getUseReactVirtualized,
  getTotalPages,
  isTwoColumnViewMode,
  getColumnMaxWidth,
  getBottomPanelHeight,
  isFitOneFullPageMode
} from "selectors/documentSelectors";
import { getAdressBarHeight } from "selectors/appUiStateSelectors";
import { isFirstExternalAPIRenditionLoaded } from "selectors/externalSelectors";
import { pageViewModifiers } from "constants/enums";
import { getPageByPageScrollTop } from "selectors/pageByPageSelectors";

export const getPageAreaRects = stores => stores.pagedViewState.pageAreaRects;
export const getPagesViewXPos = stores => stores.pagedViewState.pagesViewXPos;
export const getPagesViewYPos = stores => stores.pagedViewState.pagesViewYPos;
export const getGoToPageIndex = stores => stores.pagedViewState.goToPageIndex;

export const getThumbnailsViewYPos = stores => stores.pagedViewState.thumbnailsViewYPos;
export const isInitialPageLoaded = stores => stores.pagedViewState.isInitialPageLoaded;
export const getInitialPageIndex = stores => stores.pagedViewState.initialPageIndex;
export const isFirstPageLoaded = stores =>
  isInitialPageLoaded(stores) || isFirstDCAPIRenditionLoaded(stores) || isFirstExternalAPIRenditionLoaded(stores);
export const isPagedViewScrolling = stores => stores.pagedViewState.isPagesViewScrolling;
export const isThumbnailsViewScrolling = stores => stores.pagedViewState.isThumbnailsViewScrolling;
export const showScrubberMidDiv = stores => stores.pagedViewState.showScrubberMidDiv;
export const getPagesViewElementHandle = stores => {
  // in case of react-virtualized pages view handle is not a scrollable div and the element handle in pageviewElementHandle is
  // set react Grid component and element handle in the Grid is _scollingContainer.
  if (stores.pagedViewState.pagesViewElementHandle && stores.pagedViewState.pagesViewElementHandle._scrollingContainer)
    return stores.pagedViewState.pagesViewElementHandle._scrollingContainer;
  return stores.pagedViewState.pagesViewElementHandle;
};
export const shouldShowScrubber = stores => {
  let returnValue = false;
  if (stores.pagedViewState.showScrubber && isFirstPageLoaded(stores)) {
    returnValue = true;
  }
  return returnValue;
};
export const getTotalHeightTillPageTop = (stores, pageIndex) => {
  let returnValue = 0;
  if (pageIndex > 0) {
    const rowIndex = getRowIndex(stores, pageIndex);
    const topRow = rowIndex - 1;
    const topRowPages = getPagesInRow(stores, topRow);
    let topPageIndex = topRowPages.leftPageIndex;
    topPageIndex = topPageIndex === -1 ? 0 : topPageIndex;
    returnValue = topRow >= 0 ? getTotalHeightTillPageBottom(stores, topPageIndex) + PAGE_BOTTOM_MARGIN : 0;

    const rowHeight = getRowHeight(stores, rowIndex);
    const currentPageHeight = getPageHeight(stores, pageIndex);
    const alignmentAdjacement = (rowHeight - currentPageHeight) / 2;
    returnValue += alignmentAdjacement;
  }
  return returnValue;
};

export const getTotalHeightTillPageBottom = (stores, pageIndex) => {
  let totalHeight = constants.PAGES_TOP_PADDING;
  const rowIndex = getRowIndex(stores, pageIndex);
  const pageBottomMargin = isFitOneFullPageMode(stores) ? 0 : PAGE_BOTTOM_MARGIN;
  const viewPortHeight = getViewPortHeight(stores);
  for (let i = 0; i <= rowIndex; i++) {
    let rowHeight = getRowHeight(stores, i);
    if (isFitOneFullPageMode(stores)) {
      rowHeight = Math.max(viewPortHeight, rowHeight);
    }
    totalHeight += rowHeight;
    if (i < rowIndex) {
      totalHeight += pageBottomMargin;
    }
  }
  return totalHeight;
};

export const getPagesViewTotalHeight = stores => {
  let pagesViewHeight = getTotalHeightTillPageBottom(stores, getTotalPages(stores) - 1);
  if (isFitOneFullPageMode(stores)) {
    return pagesViewHeight;
  }
  return pagesViewHeight + PAGE_BOTTOM_MARGIN + getLastPageExtraBottomSpace(stores);
};

export const getYPosition = (stores, pageIndex, yInPage, pageBottomMargin = -1) => {
  let returnValue = 0;
  if (pageBottomMargin === -1) {
    const magnifiedYInPage = Math.ceil(yInPage * getPageZoomLevel(stores, pageIndex));
    returnValue = getTotalHeightTillPageTop(stores, pageIndex) + magnifiedYInPage;
  } else {
    returnValue = getTotalHeightTillPageBottom(stores, pageIndex) + pageBottomMargin;
  }
  return returnValue;
};

export const getXPosition = (stores, pageIndex, xInPage) => {
  let returnValue = Math.ceil(xInPage * getPageZoomLevel(stores, pageIndex));
  const columnMaxWidth = getColumnMaxWidth(stores);
  const pageWidth = getPageWidth(stores, pageIndex);
  const viewPortWidth = getViewPortWidth(stores);
  if (isTwoColumnViewMode(stores)) {
    const isHorizontalScrollPossible = 2 * columnMaxWidth - constants.TWO_COLUMN_MARGIN > viewPortWidth;
    if (!isHorizontalScrollPossible) {
      return returnValue;
    }

    /* Handling scroll offset here since Grid uses this offset through scroll left prop to decide number of column visible, which causes
    only half pages visible in two column view with cover page on first loading if offset not set accurately */
    if (!isLeftPageIndex(stores, pageIndex) && viewPortWidth > columnMaxWidth) {
      returnValue = Math.max(2 * columnMaxWidth - constants.TWO_COLUMN_MARGIN - viewPortWidth, 0); // Since TWO_COLUMN_MARGIN got added twice
    } else {
      returnValue += isLeftPageIndex(stores, pageIndex) ? columnMaxWidth - pageWidth : columnMaxWidth;
    }
  }
  return returnValue;
};

export const getThumbnailIndexForFocus = stores => stores.pagedViewState.thumbnailIndexForFocus;

export const getOpenAction = stores => toJS(stores.pagedViewState.openAction);
export const getMaxVerticalScrollSize = stores => stores.pagedViewState.verticalMaxScrollSize;

export const getPagesViewHeight = stores => {
  const pagesViewElementHandle = getPagesViewElementHandle(stores);
  if (!pagesViewElementHandle) return PAGE_BOTTOM_MARGIN;
  return pagesViewElementHandle.scrollHeight;
};

export const getPagesViewWidth = stores => {
  const pagesViewElementHandle = getPagesViewElementHandle(stores);
  if (!pagesViewElementHandle) return 0;
  return pagesViewElementHandle.scrollWidth;
};

const pagesViewStateHasHorizontalScrollbar = stores => {
  const pagesViewElementHandle = getPagesViewElementHandle(stores);
  if (!pagesViewElementHandle) return false;
  return pagesViewElementHandle.scrollWidth > pagesViewElementHandle.clientWidth;
};

export const getVerticalScrollbarSize = stores => getScrollbarSize(stores);
export const getHorizontalScrollbarSize = stores =>
  pagesViewStateHasHorizontalScrollbar(stores) ? getScrollbarSize(stores) : 0;

export const scrubberScrollHeight = stores =>
  getViewPortHeightWithoutPagesViewScrollbar(stores) -
  constants.HUD_HEIGHT -
  getAdressBarHeight(stores) -
  getBottomPanelHeight(stores);

export const pagesViewScrollHeight = stores =>
  getPagesViewHeight(stores) - getViewPortHeightWithoutPagesViewScrollbar(stores);

export const scrubberPosBasedOnPagesViewPos = stores => {
  let pagesViewYPos = 0;
  if (isFitOneFullPageMode(stores)) {
    pagesViewYPos = getPageByPageScrollTop(stores);
  } else {
    pagesViewYPos = getPagesViewYPos(stores);
  }
  let scrubberPos = (pagesViewYPos / pagesViewScrollHeight(stores)) * scrubberScrollHeight(stores);
  if (scrubberPos >= 0 && scrubberPos <= scrubberScrollHeight(stores)) {
    stores.pagedViewState.updateScrubberYPos(scrubberPos);
    return scrubberPos;
  }
  return stores.pagedViewState.scrubberYPos;
};

export const pagesViewYPosBasedOnScrubberPos = (stores, deltaY) => {
  let newScrubberPos = stores.pagedViewState.scrubberYPos + deltaY;
  const scrubberScrollHt = scrubberScrollHeight(stores);
  newScrubberPos = Math.min(Math.max(0, newScrubberPos), scrubberScrollHt);
  return (newScrubberPos / scrubberScrollHt) * pagesViewScrollHeight(stores);
};

/**
 * direction: X = 0, Y = 1
 */

const offsetHelper = (stores, containerSize, offset, toSafe, direction) => {
  if (!getUseReactVirtualized(stores)) return offset;
  const elementHandle = stores.pagedViewState.pagesViewElementHandle;
  if (!elementHandle) return offset;
  elementHandle.recomputeGridSize();
  elementHandle.measureAllCells();
  if (toSafe) {
    if (direction === 0) {
      return elementHandle._columnSizeAndPositionManager._offsetToSafeOffset({ containerSize, offset });
    }
    return elementHandle._rowSizeAndPositionManager._offsetToSafeOffset({ containerSize, offset });
  }
  if (direction === 0) {
    return elementHandle._columnSizeAndPositionManager._safeOffsetToOffset({ containerSize, offset });
  }
  return elementHandle._rowSizeAndPositionManager._safeOffsetToOffset({ containerSize, offset });
};

export const getSafeYOffset = (stores, containerSize, offset) => offsetHelper(stores, containerSize, offset, true, 1);

export const getSafeXOffset = (stores, containerSize, offset) => offsetHelper(stores, containerSize, offset, true, 0);

export const getActualYOffset = (stores, containerSize, offset) =>
  offsetHelper(stores, containerSize, offset, false, 1);

export const getActualXOffset = (stores, containerSize, offset) =>
  offsetHelper(stores, containerSize, offset, false, 0);

export const getThumbnailsViewElementHandle = stores =>
  stores.pagedViewState.thumbnailsViewElementHandle &&
  stores.pagedViewState.thumbnailsViewElementHandle._scrollingContainer;

export const getPageNumberInputHandle = stores => stores.pagedViewState.pageNumberInputHandle;
export const getIsInitialPageRendered = stores => stores.pagedViewState.isInitialPageRendered;
